if (!customElements.get('b2h-discovery')) {
  customElements.define(
    'b2h-discovery',
    class extends HTMLElement {
      disconnectedCallback() {
        this.taskController?.abort();
      }

      connectedCallback() {
        if (this.dataset.b2hDiscoveryInitialized) return;
        this.dataset.b2hDiscoveryInitialized = 'initializing';
        this.dialog = this.querySelector('[data-b2h-filter-dialog]');
        this.openButton = this.querySelector('[data-b2h-filter-open]');
        this.cancelCloseMotion = null;

        this.taskController = new AbortController();
        if (this.dataset.b2hTaskCatalog === 'all') {
          const taskKeys = new Set(['all', 'personal', 'make', 'care', 'professional']);
          const taskLinks = [...this.querySelectorAll('[data-b2h-task-link]')];
          const taskCards = [...this.querySelectorAll('[data-b2h-product-task]')];
          const resultCount = this.querySelector('[data-b2h-result-count]');
          const emptyTask = this.querySelector('[data-b2h-task-empty]');
          const signal = this.taskController.signal;
          const applyTask = () => {
            const currentUrl = new URL(window.location.href);
            const requested = currentUrl.searchParams.get('b2h_task') || 'all';
            const task = taskKeys.has(requested) ? requested : 'all';
            this.dataset.b2hActiveTask = task;
            let visibleCount = 0;
            for (const card of taskCards) {
              card.hidden = task !== 'all' && card.dataset.b2hProductTask !== task;
              if (!card.hidden) visibleCount += 1;
            }
            if (resultCount) {
              const countTemplate = visibleCount === 0 ? resultCount.dataset.resultCountZero : visibleCount === 1 ? resultCount.dataset.resultCountOne : resultCount.dataset.resultCountOther;
              resultCount.textContent = (countTemplate || resultCount.dataset.resultCountOther || '__count__').replace('__count__', String(visibleCount));
            }
            if (emptyTask) emptyTask.hidden = visibleCount !== 0;
            for (const link of taskLinks) {
              const key = link.dataset.b2hTaskLink;
              const target = new URL(currentUrl);
              target.searchParams.delete('page');
              if (key === 'all') target.searchParams.delete('b2h_task');
              else target.searchParams.set('b2h_task', key);
              link.href = target.pathname + target.search + target.hash;
              if (key === task) link.setAttribute('aria-current', 'page');
              else link.removeAttribute('aria-current');
              const counter = link.querySelector('[data-b2h-task-count]');
              if (counter) counter.textContent = String(taskCards.filter((card) => key === 'all' || card.dataset.b2hProductTask === key).length);
            }
            // Browser history can hide a card that still held keyboard focus.
            if (taskCards.some((card) => card.hidden && card.contains(document.activeElement))) {
              taskLinks.find((link) => link.dataset.b2hTaskLink === task)?.focus({ preventScroll: true });
            }
            // Preserve the selected task across native sorting and facet GET forms.
            for (const form of this.querySelectorAll('form[method="get"]')) {
              let input = form.querySelector('input[name="b2h_task"]');
              if (task === 'all') { input?.remove(); continue; }
              if (!input) { input = document.createElement('input'); input.type = 'hidden'; input.name = 'b2h_task'; form.append(input); }
              input.value = task;
            }
            for (const link of this.querySelectorAll('.b2h-discovery__active-filters a, .b2h-discovery__pagination a')) {
              const target = new URL(link.getAttribute('href'), window.location.href);
              if (target.origin !== window.location.origin) continue;
              if (task === 'all') target.searchParams.delete('b2h_task');
              else target.searchParams.set('b2h_task', task);
              link.href = target.pathname + target.search + target.hash;
            }
          };
          for (const link of taskLinks) {
            link.addEventListener('click', (event) => {
              if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
              event.preventDefault();
              window.history.pushState(null, '', link.getAttribute('href'));
              applyTask();
            }, { signal });
          }
          window.addEventListener('popstate', applyTask, { signal });
          applyTask();
        }

        // A local display preference changes geometry only, including after pagination/reload.
        const viewToggle = this.querySelector('[data-b2h-view-toggle]');
        const preferenceKey = 'b2h.collection.mobileColumns';
        const setColumns = (value) => {
          const columns = value === '1' ? '1' : '2';
          this.dataset.b2hMobileColumns = columns;
          for (const button of viewToggle?.querySelectorAll('[data-b2h-columns]') || []) {
            button.setAttribute('aria-pressed', String(button.dataset.b2hColumns === columns));
          }
        };
        if (viewToggle) {
          let storedColumns = '2';
          try { storedColumns = window.localStorage.getItem(preferenceKey) || '2'; } catch (_) { /* Storage may be unavailable. */ }
          setColumns(storedColumns);
          viewToggle.hidden = false;
          viewToggle.addEventListener('click', (event) => {
            const button = event.target.closest('[data-b2h-columns]');
            if (!button || !viewToggle.contains(button)) return;
            setColumns(button.dataset.b2hColumns);
            try { window.localStorage.setItem(preferenceKey, this.dataset.b2hMobileColumns); } catch (_) { /* Choice still works for this page. */ }
          });
        }


        const setDrawerState = (state) => {
          if (window.B2HDrawer) window.B2HDrawer.setState(this.dialog, state);
          else if (this.dialog) this.dialog.dataset.b2hDrawerState = state;
        };

        const finishClose = () => {
          if (!this.dialog?.open) return;
          this.dialog.close();
          setDrawerState('closed');
          this.openButton?.focus({ preventScroll: true });
        };

        const closeDialog = () => {
          if (!this.dialog?.open || this.dialog.dataset.b2hDrawerState === 'closing') return;
          this.cancelCloseMotion?.();
          this.cancelCloseMotion = null;
          setDrawerState('closing');
          this.cancelCloseMotion = window.B2HDrawer?.afterMotion(this.dialog, finishClose) || null;
          if (!this.cancelCloseMotion) finishClose();
        };

        this.openButton?.addEventListener('click', () => {
          if (!this.dialog || this.dialog.open) return;
          this.cancelCloseMotion?.();
          this.cancelCloseMotion = null;
          setDrawerState('closed');
          this.dialog.showModal();
          window.requestAnimationFrame(() => {
            if (!this.dialog?.open) return;
            this.dialog.getBoundingClientRect();
            setDrawerState('opening');
            this.cancelCloseMotion = window.B2HDrawer?.afterMotion(this.dialog, () => {
              if (this.dialog?.open) setDrawerState('open');
              this.cancelCloseMotion = null;
            }) || null;
            if (!this.cancelCloseMotion) setDrawerState('open');
          });
        });

        this.querySelector('[data-b2h-filter-close]')?.addEventListener('click', closeDialog);
        this.dialog?.addEventListener('click', (event) => {
          if (event.target === this.dialog) closeDialog();
        });
        this.dialog?.addEventListener('cancel', (event) => {
          event.preventDefault();
          closeDialog();
        });
        this.dialog?.addEventListener('close', () => {
          this.cancelCloseMotion?.();
          this.cancelCloseMotion = null;
          setDrawerState('closed');
        });

        this.querySelectorAll('[data-b2h-sort-select]').forEach((select) => {
          select.addEventListener('change', () => select.form?.requestSubmit());
        });

        if (this.openButton) {
          this.openButton.disabled = false;
          this.openButton.removeAttribute('aria-busy');
        }
        this.dataset.b2hDiscoveryInitialized = 'true';
      }
    }
  );
}
